import { collection, doc, getDoc, orderBy, query, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { synchronizePublicAuthor } from './publication-sync';

import type {
  AdminStats,
  BillingStore,
  PaymentHistoryItem,
  SubscriptionPlanChangePreview,
  SubscriptionPlanChangeResult,
  TargetUser,
  AccountDeletionInfo,
} from '../contracts/billing';
import type { FeatureFlags, FeatureMetadata, SubscriptionPlan, UserSubscription } from '@/types/subscription';
import type { AiGovernance } from '@common/schemas/ai-governance.schema';
import type { Firestore } from 'firebase/firestore';
import type { Functions } from 'firebase/functions';

import { SubscriptionDao } from '@/api/daos/subscription-dao';
import { fetchDocsWithCache, fetchWithCache } from '@/firebase';

export class FirebaseBillingStore implements BillingStore {
  private subDao: SubscriptionDao;

  constructor(private firestore: Firestore, private functions: Functions) {
    this.subDao = new SubscriptionDao(firestore);
  }

  subscribeSubscription(
    userId: string,
    onUpdate: (subscription: UserSubscription | null) => void,
    onError: (error: Error) => void
  ): () => void {
    return this.subDao.onSubscriptionChange(
      userId,
      (data) => {
        onUpdate(data as UserSubscription | null);
      },
      onError
    );
  }

  subscribePaymentHistory(
    userId: string,
    onUpdate: (payments: PaymentHistoryItem[]) => void
  ): () => void {
    return this.subDao.onPaymentsChange(userId, (payments) => {
      onUpdate(payments as PaymentHistoryItem[]);
    });
  }

  async fetchSubscriptionPlans(): Promise<SubscriptionPlan[]> {
    const plansQuery = query(
      collection(this.firestore, 'subscriptionPlans'),
      orderBy('displayOrder', 'asc')
    );
    const snapshot = await fetchDocsWithCache(plansQuery);
    return snapshot.docs.map((d) => {
      const planData = { ...d.data() };
      return {
        id: d.id,
        ...planData,
      } as SubscriptionPlan;
    });
  }

  async fetchGlobalFeatures(): Promise<FeatureMetadata[]> {
    const snapshot = await fetchDocsWithCache(collection(this.firestore, 'features'));
    return snapshot.docs.map((d) => d.data() as FeatureMetadata);
  }

  async fetchPlanFeatureFlags(planId: string): Promise<FeatureFlags> {
    const flagsRef = doc(this.firestore, 'subscriptionPlans', planId, 'features', 'flags');
    const snapshot = await fetchWithCache(flagsRef);
    if (snapshot.exists()) {
      return snapshot.data() as FeatureFlags;
    }
    return {};
  }

  async createPortalSession(returnUrl: string): Promise<string> {
    const fn = httpsCallable<{ returnUrl: string }, { url?: string }>(
      this.functions,
      'createPortalSession'
    );
    const result = await fn({ returnUrl });
    const url = result.data.url;
    if (!url) {
      throw new Error('No portal session URL returned');
    }
    return url;
  }

  async checkoutSession(params: {
    planId: string;
    billingInterval: 'month' | 'year';
    successUrl: string;
    cancelUrl: string;
  }): Promise<string> {
    const fn = httpsCallable<typeof params, { url?: string }>(
      this.functions,
      'createCheckoutSession'
    );
    const result = await fn(params);
    const url = result.data.url;
    if (!url) {
      throw new Error('No checkout session URL returned');
    }
    return url;
  }

  async previewPlanChange(params: {
    planId: string;
    billingInterval: 'month' | 'year';
  }): Promise<SubscriptionPlanChangePreview> {
    const fn = httpsCallable<typeof params, SubscriptionPlanChangePreview>(
      this.functions,
      'previewPlanChange0'
    );
    const result = await fn(params);
    return result.data;
  }

  async confirmPlanChange(params: {
    changeId: string;
  }): Promise<SubscriptionPlanChangeResult> {
    const fn = httpsCallable<typeof params, SubscriptionPlanChangeResult>(
      this.functions,
      'confirmPlanChange0'
    );
    const result = await fn(params);
    return result.data;
  }

  async cancelSubscription(): Promise<void> {
    const fn = httpsCallable<Record<string, never>, { success: boolean }>(
      this.functions,
      'cancelSubscription'
    );
    await fn({});
  }

  async reactivateSubscription(): Promise<void> {
    const fn = httpsCallable<Record<string, never>, { success: boolean }>(
      this.functions,
      'reactivateSubscription'
    );
    await fn({});
  }

  async syncPaymentHistory(): Promise<number> {
    const fn = httpsCallable<Record<string, never>, { success: boolean; synced?: number }>(
      this.functions,
      'getUserSubscription'
    );
    const result = await fn({});
    return result.data.synced ?? 0;
  }

  async updateProfileDisplayName(displayName: string): Promise<void> {
    const fn = httpsCallable<{ displayName: string }, { success: boolean }>(
      this.functions,
      'updateProfileDisplayName'
    );
    await fn({ displayName });
    await synchronizePublicAuthor(this.functions);
  }

  async requestContactEmailUpdate(newEmail: string): Promise<void> {
    const fn = httpsCallable<{ newEmail: string }, { success: boolean }>(
      this.functions,
      'requestContactEmailUpdate'
    );
    await fn({ newEmail });
  }

  async confirmContactEmailUpdate(code: string): Promise<void> {
    const fn = httpsCallable<{ code: string }, { success: boolean }>(
      this.functions,
      'confirmContactEmailUpdate'
    );
    await fn({ code });
  }

  async saveFeature(feature: FeatureMetadata): Promise<void> {
    const now = new Date().toISOString();
    const featureRef = doc(this.firestore, 'features', feature.key);
    await setDoc(featureRef, {
      ...feature,
      updatedAt: now,
      createdAt: feature.createdAt || now
    }, { merge: true });
  }

  async saveSubscriptionPlan(plan: SubscriptionPlan): Promise<void> {
    const now = new Date().toISOString();
    const planRef = doc(this.firestore, 'subscriptionPlans', plan.id);
    const planData: Record<string, unknown> = {
      ...plan,
      updatedAt: now,
      createdAt: plan.createdAt || now
    };
    await setDoc(planRef, planData, { merge: true });
  }

  async updatePlanFeatureFlag(planId: string, featureKey: string, value: boolean): Promise<void> {
    const flagRef = doc(this.firestore, 'subscriptionPlans', planId, 'features', 'flags');
    await setDoc(flagRef, { [featureKey]: value }, { merge: true });
  }

  async getAdminStats(): Promise<AdminStats> {
    const fn = httpsCallable<Record<string, never>, AdminStats>(
      this.functions,
      'getAdminStats'
    );
    const result = await fn({});
    return result.data;
  }

  async getUserByAdmin(targetUserId: string): Promise<TargetUser> {
    const fn = httpsCallable<{ targetUserId: string }, TargetUser>(
      this.functions,
      'getUserByAdmin'
    );
    const result = await fn({ targetUserId });
    return result.data;
  }

  async updateUserSubscription(targetUserId: string, planId: string, status: string): Promise<void> {
    const fn = httpsCallable<{ targetUserId: string; planId: string; status: string }, { success: boolean }>(
      this.functions,
      'updateUserSubscription'
    );
    await fn({ targetUserId, planId, status });
  }

  async syncAllUserFeatureFlags(): Promise<void> {
    const fn = httpsCallable<Record<string, never>, { success: boolean }>(
      this.functions,
      'syncAllUserFeatureFlags'
    );
    await fn({});
  }

  async requestAccountDeletion(
    confirmation: string,
    locale: string
  ): Promise<{ status: 'pending_deletion'; scheduledFor: string }> {
    const fn = httpsCallable<
      { confirmation: string; locale: string },
      { status: 'pending_deletion'; scheduledFor: string }
    >(
      this.functions,
      'requestAccountDeletion'
    );
    return (await fn({ confirmation, locale })).data;
  }

  async resendAccountDeletionEmail(): Promise<void> {
    const fn = httpsCallable<Record<string, never>, { sent: true }>(
      this.functions,
      'resendAccountDeletionEmail'
    );
    await fn({});
  }

  async restoreAccountDeletion(token: string): Promise<void> {
    const fn = httpsCallable<{ token: string }, { restored: true }>(
      this.functions,
      'restoreAccountDeletion'
    );
    await fn({ token });
  }

  async getAccountDeletionStatus(): Promise<AccountDeletionInfo> {
    const fn = httpsCallable<Record<string, never>, AccountDeletionInfo>(
      this.functions,
      'getAccountDeletionStatus'
    );
    return (await fn({})).data;
  }

  async getAiGovernance(): Promise<AiGovernance | null> {
    const docRef = doc(this.firestore, 'config', 'ai-governance');
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      return snap.data() as AiGovernance;
    }
    return null;
  }

  async saveAiGovernance(governance: AiGovernance): Promise<void> {
    const docRef = doc(this.firestore, 'config', 'ai-governance');
    await setDoc(docRef, governance);
  }
}
